import { generateRandomId } from "./Equation";

const addTableFootnote = (editor, note, notes) => {
  const tableWrap = editor.dom.getParent(note.node, "table-wrap");
  const tableFoot = tableWrap?.querySelector("table-wrap-foot");

  if (tableFoot) {
    // Table already has footnotes, add a new <fn>
    const foot = tableFoot.querySelectorAll("fn");
    const lastFn = foot[foot.length - 1];
    const lastId = parseInt(lastFn.getAttribute("id").replace(/\D/g, "")) || 0;
    const id = tableWrap.getAttribute("id");

    const fn = document.createElement("fn");
    let FNkey = generateRandomId("TFN2_");
    fn.setAttribute("key", FNkey);
    fn.setAttribute("id", `${id}fn${lastId + 1}`);
    fn.setAttribute("fn-type", "other");

    const p = document.createElement("p");
    let FPkey = generateRandomId("TFNP2_");
    p.setAttribute("key", FPkey);
    p.textContent = notes;
    fn.appendChild(p);

    tableFoot.appendChild(fn);
    return true;
  } else if (tableWrap) {
    // No footnotes yet, create <table-wrap-foot>
    const id = tableWrap.getAttribute("id");
    const foot = document.createElement("table-wrap-foot");
    let Fkey = generateRandomId("TFNF_");
    foot.setAttribute("key", Fkey);

    const fn1 = document.createElement("fn");
    let FNkey = generateRandomId("TFN_");
    fn1.setAttribute("key", FNkey);
    fn1.setAttribute("id", `${id}fn`);

    const p1 = document.createElement("p");
    let FPkey = generateRandomId("TFNP_");
    p1.setAttribute("key", FPkey);

    const bold = document.createElement("bold");
    let FBkey = generateRandomId("TFNB_");
    bold.setAttribute("key", FBkey);
    bold.textContent = "Note:";
    p1.appendChild(bold);
    fn1.appendChild(p1);

    const fn2 = document.createElement("fn");
    let FN2key = generateRandomId("TFN_");
    fn2.setAttribute("key", FN2key);
    fn2.setAttribute("id", `${id}fn1`);
    fn2.setAttribute("fn-type", "other");

    const p2 = document.createElement("p");
    let FP2key = generateRandomId("TFNP_");
    p2.setAttribute("key", FP2key);
    p2.textContent = notes;
    fn2.appendChild(p2);

    foot.appendChild(fn1);
    foot.appendChild(fn2);

    tableWrap.appendChild(foot);
    return true;
  }
};

const addTextFootnote = (editor, note, notes) => {
  const foot = document.createElement("fn");
  let Fkey = generateRandomId("FN_");
  foot.setAttribute("key", Fkey);

  const label = document.createElement("label");
  let FLkey = generateRandomId("FNL_");
  label.setAttribute("key", FLkey);

  const p = document.createElement("p");
  let Pkey = generateRandomId("FNP_");
  p.setAttribute("key", Pkey);

  const existingFns = editor.getBody().querySelectorAll("fn");
  let maxNumber = 0;

  // Find the highest existing fn number
  existingFns.forEach((fn) => {
    const id = fn.getAttribute("id");
    const match = id && id.match(/^fn-(\d+)$/);
    if (match) {
      const num = parseInt(match[1]);
      if (num > maxNumber) maxNumber = num;
    }
  });

  const newFnNumber = maxNumber + 1;
  foot.setAttribute("id", `fn-${newFnNumber}`);
  label.innerHTML = newFnNumber;
  foot.appendChild(label);

  p.innerHTML = notes;
  foot.appendChild(p);

  if (
    note.node.nextSibling &&
    note.node.nextSibling.tagName.toLowerCase() === "fn"
  ) {
    note.node.nextSibling.insertAdjacentElement("afterend", foot);
  } else {
    note.node.insertAdjacentElement("afterend", foot);
  }
  return true;
};

const convertHypenToMinus = (editor, table) => {
  if (table) {
    table.querySelectorAll("*").forEach((el) => {
      el.childNodes.forEach((child) => {
        if (child.nodeType === Node.TEXT_NODE) {
          // Replace hyphen (-) with Unicode minus (−) only if it is followed by a number
          child.textContent = child.textContent.replace(
            /-\s*(\d)/g,
            "\u2212 $1"
          );
        }
      });
    });

    editor.undoManager.transact(() => {
      editor.dom.replace(table, table.cloneNode(true));
    });
  }
};

function handleEnterInTd(editor, event) {
  const rng = editor.selection.getRng();
  const node = editor.selection.getNode();
  const td = editor.dom.getParent(node, "td");
  const tr = editor.dom.getParent(td, "tr");

  if (!td) {
    setTimeout(() => {
      const selectionNode = editor.selection.getNode();
      if (selectionNode && selectionNode.hasAttribute("key")) {
        selectionNode.setAttribute("key", generateRandomId("_"));
      }
    }, 0);
    return; // Only handle inside <td>
  }

  event.preventDefault(); // prevent default Enter behavior

  const newP = document.createElement("p");
  const newTr = document.createElement("tr");
  newTr.setAttribute("align", "center");
  const newTd = document.createElement("td");
  if (rng.startContainer.nodeType === Node.TEXT_NODE) {
    // Caret is inside a text node
    const textNode = rng.startContainer;
    const afterText = textNode.splitText(rng.startOffset);

    // Check if parent tag (like <b>, <i>) exists
    let parentTag = textNode.parentNode;
    if (parentTag.nodeType === Node.ELEMENT_NODE && parentTag !== td) {
      const clonedParent = parentTag.cloneNode(false);
      clonedParent.appendChild(afterText);
      newP.appendChild(clonedParent);

      // Move siblings after parentTag into <p>
      let nextNode = parentTag.nextSibling;
      while (nextNode) {
        const nodeToMove = nextNode;
        nextNode = nextNode.nextSibling;
        newP.appendChild(nodeToMove);
      }
    } else {
      newP.appendChild(afterText);

      let nextNode = textNode.nextSibling;
      while (nextNode) {
        const nodeToMove = nextNode;
        nextNode = nextNode.nextSibling;
        newP.appendChild(nodeToMove);
      }
    }
  } else if (rng.startContainer.nodeType === Node.ELEMENT_NODE) {
    let nextNode = rng.startContainer.childNodes[rng.startOffset];
    while (nextNode) {
      const nodeToMove = nextNode;
      nextNode = nextNode.nextSibling;
      newP.appendChild(nodeToMove);
    }
  }
  newTd.setAttribute("colspan", "3");

  newTd.innerHTML = newP.innerHTML;
  newTr.appendChild(newTd);
  tr.insertAdjacentElement("afterend", newTr);
  // td.appendChild(newP)
  editor.selection.setCursorLocation(newP, 0);
}

function splitTdOnBr(table) {
  const rows = table.querySelectorAll("tr");

  rows.forEach((row) => {
    const tds = row.querySelectorAll("td");

    tds.forEach((td) => {
      const brs = Array.from(td.querySelectorAll("br"));

      brs.forEach((br) => {
        const parentTr = td.closest("tr");
        const newTr = document.createElement("tr");
        const newTd = document.createElement("td");
        if (td.hasAttribute("colspan")) {
          newTd.setAttribute("colspan", "3");
          newTd.setAttribute("align", "center");
        }

        let sibling = br.nextSibling;
        while (sibling) {
          const nextSibling = sibling.nextSibling;
          newTd.appendChild(sibling);
          sibling = nextSibling;
        }

        br.remove();

        newTr.appendChild(newTd);

        parentTr.parentNode.insertBefore(newTr, parentTr.nextSibling);
      });
    });
  });
}
const handleCreateEmptyTable = (editor, rows = 2, cols = 3) => {
  const allTable = editor.dom
    .select("table-wrap")
    .map((el) => el.getAttribute("id"))
    .filter((id) => id)
    .sort((a, b) => {
      const numA = parseInt(a.replace(/\D/g, ""), 10);
      const numB = parseInt(b.replace(/\D/g, ""), 10);
      return numA - numB;
    });

  const lastId = allTable.length > 0 ? allTable[allTable.length - 1] : null;
  const lastNum = lastId ? parseInt(lastId.replace(/\D/g, ""), 10) + 1 : 1;
  const newId = `table-${lastNum}`;

  const tableWrap = document.createElement("table-wrap");
  tableWrap.setAttribute("id", newId);
  tableWrap.setAttribute("key", generateRandomId("twra_"));

  const label = document.createElement("label");
  label.setAttribute("key", generateRandomId("label_"));
  label.textContent = `Table ${lastNum}`;

  const title1 = document.createElement("title1");
  title1.setAttribute("key", generateRandomId("title_"));
  title1.textContent = "Caption";

  const alternatives = document.createElement("alternatives");
  alternatives.setAttribute("key", generateRandomId("alter_"));

  const table = document.createElement("table");
  table.setAttribute("key", generateRandomId("table_"));
  table.classList.add("mce-item-table");

  const colgroup = document.createElement("colgroup");
  colgroup.setAttribute("key", generateRandomId("colgroup_"));

  for (let i = 0; i < cols; i++) {
    const col = document.createElement("col");
    col.setAttribute("align", "center");
    col.setAttribute("key", generateRandomId("col_"));
    colgroup.appendChild(col);
  }

  const thead = document.createElement("thead");
  thead.setAttribute("key", generateRandomId("thead_"));
  const headRow = document.createElement("tr");
  headRow.setAttribute("align", "center");
  headRow.setAttribute("key", generateRandomId("tr_"));

  for (let i = 0; i < cols; i++) {
    const th = document.createElement("th");
    th.setAttribute("key", generateRandomId("th_"));
    th.textContent = `Header ${i + 1}`;
    headRow.appendChild(th);
  }
  thead.appendChild(headRow);

  const tbody = document.createElement("tbody");
  tbody.setAttribute("key", generateRandomId("tbody_"));

  for (let r = 0; r < rows; r++) {
    const bodyRow = document.createElement("tr");
    bodyRow.setAttribute("align", "center");
    bodyRow.setAttribute("key", generateRandomId("tr_"));
    for (let c = 0; c < cols; c++) {
      const td = document.createElement("td");
      td.setAttribute("key", generateRandomId("td_"));
      td.innerHTML = "";
      bodyRow.appendChild(td);
    }
    tbody.appendChild(bodyRow);
  }

  table.appendChild(colgroup);
  table.appendChild(thead);
  table.appendChild(tbody);

  alternatives.appendChild(table);
  tableWrap.appendChild(label);
  tableWrap.appendChild(title1);
  tableWrap.appendChild(alternatives);
  const selectedNode = editor.selection.getNode();
  if (selectedNode && selectedNode.parentNode) {
    const nextElement = selectedNode.nextSibling;
    if (nextElement) {
      if (nextElement.tagName.toLowerCase() == "title1") {
        selectedNode.parentNode.insertBefore(
          tableWrap,
          nextElement.nextSibling
        );
      } else {
        selectedNode.parentNode.insertBefore(tableWrap, nextElement);
      }
    } else {
      selectedNode.parentNode.appendChild(tableWrap);
    }
  }
};

export {
  addTableFootnote,
  addTextFootnote,
  convertHypenToMinus,
  handleEnterInTd,
  splitTdOnBr,
  handleCreateEmptyTable,
};
